<template>
  <div v-loading="loading">
    <!-- Chat Toggle Button -->
    <div class="chat-toggle" @click="toggleChat">
      Intera Chat <span class="arrow">{{ isChatOpen ? "▼" : "▲" }}</span>
      <span class="online-indicator">● Online</span>
    </div>

    <!-- Chat Box -->
    <div class="chat-container" :class="{ open: isChatOpen }">
      <!-- Header -->
      <div class="chat-header">
        <span>Chat <span class="online-indicator">● Online</span></span>

        <div class="button-group">
          <button @click="goBackToChat" title="Back to Chat">⬅️</button>
          <button @click="toggleChat" title="Minimize Chat">➖</button>
          <button @click="closeChat" title="Close Chat">✖</button>
        </div>
      </div>

      <div class="chat-body">
        <!-- Show Chat Form if No Chat is Active -->
        <div v-if="!chatUrl">
          <!-- User Selection Dropdown -->
          <div class="chat-form">
            <div class="form-group">
              <label class="form-label"><b>Select Users to Chat</b></label>
              <el-select
                v-model="selectedUsers"
                filterable
                multiple
                clearable
                remote
                reserve-keyword
                placeholder="Search users..."
                :remote-method="filterUsers"
                :loading="selectLoading"
                class="user-select"
              >
                <el-option
                  v-for="user in filteredUsers"
                  :key="user._id"
                  :label="`${user.first_name} ${user.last_name}`"
                  :value="user._id"
                />
              </el-select>
            </div>

            <!-- Create Chat Room Button -->
            <button
              class="chat-btn"
              @click="createChatRoom"
              :disabled="selectedUsers.length < 1 || creatingRoom"
            >
              <span>Start Conversation</span>
            </button>
          </div>

          <!-- Chat History -->

          <div class="chat-history">
            <h3>Recent</h3>

            <!-- Search Bar -->
            <div class="search-container">
              <input
                type="text"
                v-model="searchQuery"
                @input="filterChatHistory"
                placeholder="Search chat history..."
                class="chat-search"
              />
              <span class="search-icon">🔍</span>
            </div>

            <div v-if="filteredRooms.length > 0" class="chat-list">
              <div
                v-for="room in filteredRooms"
                :key="room.roomID"
                class="chat-card"
                @click="openChat(room.roomID)"
              >
                <div :title="room.users.join(', ')" class="chat-item">
                  <div class="user-avatar">
                    <template v-if="room.users.length === 2">
                      {{
                        room.users
                          .find(
                            (user) =>
                              user !==
                              getProfile.first_name + " " + getProfile.last_name
                          )
                          .charAt(0)
                      }}
                    </template>
                    <template v-else>
                      {{
                        getInitials(
                          room.users.filter(
                            (user) =>
                              user !==
                              getProfile.first_name + " " + getProfile.last_name
                          )
                        )
                      }}
                    </template>
                  </div>
                  <div class="chat-details">
                    <span v-if="room.users.length > 2" class="group-name">
                      {{
                        room.roomName.charAt(0).toUpperCase() +
                        room.roomName.slice(1)
                      }}
                    </span>
                    <span class="chat-name">
                      <template v-if="room.users.length === 2">
                        {{
                          room.users.find(
                            (user) =>
                              user !==
                              getProfile.first_name + " " + getProfile.last_name
                          )
                        }}
                      </template>
                      <template v-else>
                        You and {{ room.users.length - 1 }} others
                      </template>
                    </span>
                  </div>
                  <div class="chat-meta">
                    <span class="timestamp">
                      {{ formatTimestamp(room.lastMessageTimestamp) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <p v-else class="no-chats">No matching chats found.</p>
          </div>
        </div>

        <!-- Embedded Chat Room -->
        <div v-if="chatUrl" class="chat-frame-container">
          <iframe
            :src="chatUrl"
            class="chat-frame"
            @load="onIframeLoaded"
            @error="onIframeError"
          ></iframe>
          <div v-if="iframeLoading" class="iframe-loader">
            <div class="loader"></div>
            <p>Loading chat room...</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";

export default {
  data() {
    return {
      searchQuery: "", // Stores the user's search query for filtering
      filteredRooms: [], // Stores the filtered chat rooms
      allRooms: [], // Stores all chat rooms
      displayRooms: [], // Stores rooms for the current page
      currentPage: 1, // Current page number
      itemsPerPage: 5, // Rooms per page
      isChatOpen: false,
      selectedUsers: [],
      users: [],
      filteredUsers: [],
      loading: true,
      selectLoading: false,
      creatingRoom: false,
      chatRoomId: null,
      chatUrl: "",
      iframeLoading: false,
      historyMessages: [
        "Support - Last chat: 2h ago",
        "Tech Support - 3 days ago",
      ],
    };
  },

  computed: {
    ...mapGetters("auth", ["getProfile", "getActiveWorkspace"]),
    ...mapGetters("auth", ["getAuthenticatedUser", "getCompanyUsers"]),
    totalPages() {
      return Math.ceil(this.allRooms.length / this.itemsPerPage);
    },
  },
  created() {
    this.userInfo = this.getAuthenticatedUser;
  },

  async mounted() {
    this.filteredRooms = [...this.allRooms]; // Show all rooms initially
    this.displayRooms = [...this.allRooms]; // Show all rooms initially in pagination
    try {
      await this.getCompanyUsersList();
      await this.getChatHistory();
      console.log("this.getProfile", this.getProfile);
    } catch (error) {
      console.error("Failed to load users:", error);
      this.$notify.error({ title: "Error", message: "Failed to load users" });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    filterChatHistory() {
      const query = this.searchQuery.trim().toLowerCase();

      if (query === "") {
        this.filteredRooms = [...this.allRooms]; // Show all rooms when search is empty
      } else {
        this.filteredRooms = this.allRooms.filter(
          (room) =>
            room.roomName.toLowerCase().includes(query) ||
            room.users.some((user) => user.toLowerCase().includes(query))
        );
      }
    },
    closeChat() {
      this.isChatOpen = false;
      this.chatUrl = ""; // Optional: Clear chat URL when closing
      this.selectedUsers = []; // Clear selected users when closing chat
    },

    goBackToChat() {
      this.chatUrl = ""; // Optional: Clear chat URL when closing
      this.selectedUsers = []; // Clear selected users when closing chat
    },

    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen;
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      const now = new Date();

      const isToday = date.toDateString() === now.toDateString();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();

      if (isToday) {
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      } else if (isYesterday) {
        return "Yesterday";
      } else {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
    },
    getInitials(users) {
      return users.map((user) => user.charAt(0).toUpperCase()).join("");
    },
    openChat(roomID) {
      this.iframeLoading = true;
      this.chatRoomId = roomID;

      const primaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--primary-color")
        .trim();
      const hexColor = primaryColor.startsWith("#")
        ? primaryColor.slice(1) // Removes #
        : primaryColor
            .match(/\d+/g)
            .map((x) => (+x).toString(16).padStart(2, "0"))
            .join("")
            .toUpperCase();

      this.chatUrl = `https://chat.nimbleio.ai/chat?authID=21cc5e96f2736c81673fb94587c90b87c90b7f5dbd03780039349dfeb1e3457d&roomID=${roomID}&userID=${
        this.getProfile._id + this.getActiveWorkspace.company_id
      }&primary=${hexColor}`;
      // this.iframeLoading = false;
    },
    async getChatHistory() {
      try {
        const userID = this.getProfile._id;
        const companyID = this.getActiveWorkspace.company_id;
        const url = `https://chat.nimbleio.ai/api/fetch_rooms_by_user_id?user_id=${userID}${companyID}`;

        const response = await axios.get(url, {
          headers: {
            accept: "application/json",
            Authorization: `Bearer YOUR_ACCESS_TOKEN`,
          },
        });

        if (response.data && response.data.length > 0) {
          // Sort the rooms by date in descending order
          this.allRooms = response.data.sort(
            (a, b) =>
              new Date(b.lastMessageTimestamp) -
              new Date(a.lastMessageTimestamp)
          );

          this.filteredRooms = [...this.allRooms]; // Ensure filteredRooms is updated
          this.displayRooms = [...this.allRooms]; // Show all rooms initially
          this.paginateRooms(); // Initialize pagination
        } else {
          this.allRooms = [];
          this.filteredRooms = [];
          this.displayRooms = [];
        }
      } catch (error) {
        console.error("Failed to fetch chat history:", error);
        this.allRooms = [];
        this.filteredRooms = [];
        this.displayRooms = [];
      }
    },

    paginateRooms() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      this.displayRooms = this.allRooms.slice(start, end);
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.paginateRooms();
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.paginateRooms();
      }
    },

    goToPage(page) {
      this.currentPage = page;
      this.paginateRooms();
    },

    toggleChat() {
      this.isChatOpen = !this.isChatOpen;
    },
    async createChatRoom() {
      if (this.selectedUsers.length === 0) {
        this.$notify.error({
          title: "Error",
          message: "Select at least one user",
        });
        return;
      }

      this.creatingRoom = true;
      this.loading = true;
      this.iframeLoading = false;

      const participants = [
        {
          username: this.getProfile.first_name.toLowerCase(),
          userID: this.getProfile._id + this.getActiveWorkspace.company_id,
          displayName: `${this.getProfile.first_name} ${this.getProfile.last_name}`,
          gender: this.getProfile.gender || "male",
        },
        ...this.selectedUsers.map((userId) => {
          const user = this.users.find((u) => u._id === userId);
          return {
            username: user.first_name.toLowerCase(),
            userID: user._id + this.getActiveWorkspace.company_id,
            displayName: `${user.first_name} ${user.last_name}`,
            gender: user.gender || "male",
          };
        }),
      ];

      try {
        const response = await axios.post(
          "https://chat.nimbleio.ai/api/create_room?room_name=groupMeeting",
          {
            participates: participants,
            room_details: {},
          }
        );

        if (response.data) {
          this.chatRoomId = response.data;

          const primaryColor = getComputedStyle(document.documentElement)
            .getPropertyValue("--primary-color")
            .trim();
          const hexColor = primaryColor.startsWith("#")
            ? primaryColor.slice(1) // Removes #
            : primaryColor
                .match(/\d+/g)
                .map((x) => (+x).toString(16).padStart(2, "0"))
                .join("")
                .toUpperCase();

          this.chatUrl = `https://chat.nimbleio.ai/chat?authID=21cc5e96f2736c81673fb94587c90b87c90b7f5dbd03780039349dfeb1e3457d&roomID=${
            this.chatRoomId
          }&userID=${
            this.getProfile._id + this.getActiveWorkspace.company_id
          }&primary=${hexColor}`;

          this.iframeLoading = true;
          this.loading = false;
          this.creatingRoom = false;

          this.$notify.success({
            title: "Success",
            message: "Chat Room Created! Loading chat interface...",
          });
        }
      } catch (error) {
        console.error("Chat room creation failed:", error);
        this.$notify.error({
          title: "Error",
          message: "Failed to create chat room",
        });
        this.loading = false;
        this.creatingRoom = false;
        this.iframeLoading = false;
      }
    },
    onIframeLoaded() {
      console.log("Chat iframe loaded successfully");
      this.iframeLoading = false;
    },
    onIframeError() {
      console.error("Failed to load chat iframe");
      this.$notify.error({
        title: "Error",
        message: "Failed to load chat interface",
      });
      this.iframeLoading = false;
      this.chatUrl = "";
    },
    async getCompanyUsersList() {
      try {
        await this.$store.dispatch(
          "auth/getCompanyUsersList",
          this.userInfo.company_id
        );
        if (this.getCompanyUsers && this.getCompanyUsers.data) {
          this.users = this.getCompanyUsers.data;
          this.filteredUsers = this.users.filter(
            (user) => user._id !== this.getProfile._id
          );
        }
      } catch (err) {
        console.error("Failed to load company users:", err);
        throw err;
      }
    },
    filterUsers(query) {
      if (query) {
        this.filteredUsers = this.users.filter((user) =>
          `${user.first_name} ${user.last_name}`
            .toLowerCase()
            .includes(query.toLowerCase())
        );
      } else {
        this.filteredUsers = [...this.users];
      }
    },
  },
};
</script>

<style scoped>
.chat-history {
  /* max-width: 400px; */
  margin: auto;
  padding: 15px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
}

/* Search Bar Styling */
.search-container {
  position: relative;
  width: 100%;
}

.chat-search {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
  transition: 0.3s ease-in-out;
}

.chat-search:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0px 0px 5px rgba(0, 123, 255, 0.5);
}

.search-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 16px;
}

/* Chat List Scrollable */
.chat-list {
  max-height: 50vh;
  overflow-y: auto;
  padding-right: 5px;
}

.chat-list::-webkit-scrollbar {
  width: 6px;
}

.chat-list::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 5px;
}

.chat-card {
  padding: 10px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.3s ease;
}

.chat-card:hover {
  background: #f5f5f5;
}

.user-avatar {
  width: 40px;
  height: 40px;
  background: #007bff;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: 10px;
}

.chat-details {
  flex-grow: 1;
}

.chat-name {
  font-weight: bold;
}

.chat-meta {
  font-size: 12px;
  color: #777;
}

.no-chats {
  text-align: center;
  color: #999;
  padding: 15px 0;
}

/* Search Bar Styling */

.search-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 16px;
}

/* Chat List Scrollable */
/* .chat-list {
  max-height: 80vh;
  overflow-y: auto;
  padding-right: 5px;
} */

.chat-list::-webkit-scrollbar {
  width: 6px;
}

.chat-list::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 5px;
}

.chat-card {
  padding: 10px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.3s ease;
}

.chat-card:hover {
  background: #f5f5f5;
}

.user-avatar {
  width: 40px;
  height: 40px;
  background: #007bff;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: 10px;
}

.chat-details {
  flex-grow: 1;
}

.chat-name {
  font-weight: bold;
}

.chat-meta {
  font-size: 12px;
  color: #777;
}

.no-chats {
  text-align: center;
  color: #999;
  padding: 15px 0;
}

.chat-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* padding: 14px;
  border-bottom: 1px solid #eee; */
  width: 100%;
}

.user-avatar {
  width: 50px;
  height: 50px;
  background-color: #007bff;
  color: white;
  font-size: 18px;
  font-weight: bold;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.chat-details {
  flex-grow: 1;
  padding: 0 10px;
  display: flex;
  flex-direction: column;
}

.chat-name {
  font-weight: bold;
  font-size: 16px;
  color: #222;
}

.user-count {
  font-size: 13px;
  color: gray;
}

.chat-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
}

.timestamp {
  font-size: 12px;
  color: gray;
}

/* Chat History */

.chat-history h3 {
  font-size: 18px;
  color: #333;
  margin-bottom: 10px;
}

/* Chat List */
.chat-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Individual Chat Card */
.chat-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 12px 15px;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid #ddd;
  cursor: pointer;
}

.chat-card:hover {
  background: #f1f1f1;
}

/* Chat Name */
.chat-name {
  font-weight: bold;
  color: #0078d7;
}

/* Chat ID */
.chat-id {
  font-size: 12px;
  color: #666;
}

/* No Chats */
.no-chats {
  color: #888;
  text-align: center;
  margin-top: 10px;
}

/* Chat Container */
.chat-container {
  margin-top: 15px;
  width: 100%;
  height: 500px;
}

.chat-container iframe {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

/* Chat History */

.chat-history h3 {
  font-size: 18px;
  color: #333;
  margin-bottom: 10px;
}

/* Chat List */
.chat-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Individual Chat Card */
.chat-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 12px 15px;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid #ddd;
  cursor: pointer;
}

.chat-card:hover {
  background: #f1f1f1;
}

/* Chat Name */
.chat-name {
  font-weight: bold;
  color: #0078d7;
}

/* Chat ID */
.chat-id {
  font-size: 12px;
  color: #666;
}

/* No Chats */
.no-chats {
  color: #888;
  text-align: center;
  margin-top: 10px;
}

.chat-form {
  padding: 1%;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px; /* Adds spacing between label and field */
}

/* Close Button */
.chat-header {
  display: flex;
  align-items: center; /* Ensures vertical alignment */
  justify-content: space-between; /* Positions elements properly */
  padding: 10px;
  background: #007bff;
  color: white;
}

.chat-header button {
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  margin-left: 5px; /* Adjust this if needed */
}

/* Wrap buttons in a container */
.button-group {
  display: flex;
  gap: 5px; /* Controls spacing between buttons */
}

.chat-header button {
  background: none;
  border: none;
  font-size: 16px;
  color: #ff4d4f;
  cursor: pointer;
  padding: 5px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s ease-in-out;
}

.chat-header button:hover {
  background: rgba(255, 77, 79, 0.2);
}

/* Loader */
.iframe-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.iframe-loader .loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0078d7;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.iframe-loader p {
  color: #0078d7;
  font-weight: bold;
}

/* Chat Toggle */
.chat-toggle {
  position: fixed;
  bottom: 0;
  right: 20px;
  width: 220px;
  height: 45px;
  background: var(--primary-color) !important;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease-in-out;
}

.arrow {
  margin-left: 8px;
}

/* Chat Container */
.chat-container {
  position: fixed;
  bottom: 0;
  right: 20px;
  width: 0;
  height: 90vh;
  background: #ffffff;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  transition: width 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  z-index: 9999999;
}

.chat-container.open {
  padding: 2px;
  width: 35%;
}

/* Chat Header */
.chat-header {
  background: #f4f4f4;
  color: #ffa500;
  padding: 1%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
  border-radius: 12px 12px 0 0;
}

/* Chat Frame */
.chat-frame-container {
  position: relative;
  width: 100%;
  height: 80vh;
  border-radius: 8px;
  overflow: hidden;
}

.chat-frame {
  width: 100%;
  height: 100%;
  border: none;
}

/* Online Indicator */
.online-indicator {
  font-size: 14px;
  margin-left: 8px;
  color: #90ee90;
}

/* Chat History */
.chat-history {
  background: #f5f5f5;
  padding: 12px;
  margin-top: 15px;
  border-radius: 8px;
  font-size: 14px;
  color: #555;
  /* max-height: 200px; */
  /* overflow-y: auto; */
}

.chat-history h4 {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
}

.chat-history ul {
  list-style: none;
  padding: 0;
}

.chat-message {
  background: #e1eaf6;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 5px;
}

/* Chat Button */
.chat-btn {
  margin-top: 2%;
  width: 100%;
  background: #0078d7;
  color: white;
  border: none;
  padding: 10px 15px;
  cursor: pointer;
  border-radius: 8px;
  font-weight: bold;
}

.chat-btn:disabled {
  background: #bbb;
  cursor: not-allowed;
}

/* Spinner Animation */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 📱 Mobile Responsiveness */

@media screen and (max-width: 375px) {
  .chat-container {
    display: none; /* keep it closed initially on mobile too */
    z-index: 9999999 !important;
  }
}
@media screen and (max-width: 768px) {
  .chat-container {
    display: none; /* keep it closed initially on mobile too */
    z-index: 9999999;
  }

  .chat-container.open {
    display: block;
  }
  .chat-list {
    max-height: 50vh;
    overflow-y: auto;
    padding-right: 5px;
  }
  .chat-container {
    width: 100%;
    height: 90vh !important; /* Full height */
    border-radius: 0;
    bottom: 0;
    right: 0;
    overflow-y: auto; /* Enables scrolling */
  }

  .chat-frame-container {
    height: calc(100vh - 50px); /* Adjust for header */
  }

  .chat-frame {
    height: 100%;
  }

  .chat-container.open {
    width: 100%;
    height: 80vh;
    padding: 0;
  }

  .chat-toggle {
    width: 100%;
    right: 0;
    bottom: 0;
    border-radius: 0;
  }
  .chat-toggle:focus {
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.5);
  }

  .chat-header {
    font-size: 16px;
    padding: 10px;
  }

  .iframe-loader {
    height: 80vh;
  }

  .chat-box {
    display: none;
    position: fixed;
    bottom: 80px;
    right: 10px;
    width: 90%;
    max-height: 80%;
  }
}
</style>
